'use client';

import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

interface UseEmbedAutoHeightParams {
  /** Ref to the iframe whose content reports its height. */
  iframeRef: RefObject<HTMLIFrameElement | null>;
  /** Expected `event.origin` of the provider's resize messages. */
  origin: string;
  /** Extracts a pixel height from a provider message payload, or null. */
  getHeight: (data: unknown) => number | null;
}

/**
 * Listens for a provider's cross-origin "content height" message and returns the
 * latest reported height. Some embed iframes (Everviz, TikTok) broadcast their
 * rendered content height via `postMessage` so the host can size the frame to
 * the content instead of forcing a fixed aspect ratio — which is what keeps
 * responsive charts/cards from being clipped or padded with whitespace on
 * narrow viewports.
 *
 * Hardened: only messages from the matching `origin` AND originating from this
 * iframe's own `contentWindow` are honored, so sibling embeds can't size each
 * other. Returns null until a valid height arrives, letting callers fall back to
 * an aspect-ratio frame in the meantime.
 */
export function useEmbedAutoHeight({
  iframeRef,
  origin,
  getHeight,
}: UseEmbedAutoHeightParams): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== origin) return;
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      const next = getHeight(event.data);
      if (next != null && Number.isFinite(next) && next > 0) {
        setHeight(Math.ceil(next));
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef, origin, getHeight]);

  return height;
}
